import { loadBusinessCenterOperationalState, saveBusinessCenterOperationalState } from "./businessCenterOperationalStateApi";
import {
  appendARRecoveryActivityNote,
  appendCollectionLetterHistory,
  appendUniversalActivity,
  appendUniversalAuditEntry,
  createUniversalOperationalFoundationState,
  loadUniversalOperationalFoundationState,
  saveUniversalOperationalFoundationState,
  upsertCollectionLetterDraft,
  upsertUniversalBusinessNote,
  upsertUniversalTickler,
  getActivityByRecord,
  getAuditByRecord,
  getCollectionLetterHistoryByAccount,
  getNotesByRecord,
  getTicklersByRecord,
  type ARRecoveryActivityNote,
  type UniversalActivityEvent,
  type UniversalAuditTrailEntry,
  type UniversalBusinessNote,
  type UniversalCollectionLetterDraft,
  type UniversalCollectionLetterHistoryEntry,
  type UniversalOperationalFoundationState,
  type UniversalTickler,
  type UniversalRecordType,
  type UniversalWorkspaceKey,
} from "./universalOperationalEngines";

export type OperationalPersistenceSource = "backend" | "local";

export interface OperationalPersistenceResult<T = UniversalOperationalFoundationState> {
  state: T;
  source: OperationalPersistenceSource;
  backendAvailable: boolean;
  error?: string;
}

const UNIVERSAL_OPERATIONAL_MODULE_KEY = "universal-operational-foundation";
const UNIVERSAL_OPERATIONAL_SCOPE = {
  scopeType: "platform" as const,
  stateKey: "universal-operational-foundation-v1",
};

export async function loadOperationalState(): Promise<OperationalPersistenceResult> {
  const local = loadUniversalOperationalFoundationState();
  try {
    const response = await loadBusinessCenterOperationalState<UniversalOperationalFoundationState>(
      UNIVERSAL_OPERATIONAL_MODULE_KEY,
      UNIVERSAL_OPERATIONAL_SCOPE,
    );
    const remote = response.state ? createUniversalOperationalFoundationState(response.state) : createUniversalOperationalFoundationState();
    const merged = mergeOperationalStates(remote, local);
    saveUniversalOperationalFoundationState(merged);
    if (!response.state || !isOperationalStateEmpty(local)) {
      void saveBackendOperationalState(merged);
    }
    return { state: merged, source: "backend", backendAvailable: true };
  } catch (error) {
    return {
      state: local,
      source: "local",
      backendAvailable: false,
      error: error instanceof Error ? error.message : "Backend operational state unavailable.",
    };
  }
}

export async function saveOperationalState(state: UniversalOperationalFoundationState): Promise<OperationalPersistenceResult> {
  const normalized = createUniversalOperationalFoundationState(state);
  saveUniversalOperationalFoundationState(normalized);
  try {
    await saveBackendOperationalState(normalized);
    return { state: normalized, source: "backend", backendAvailable: true };
  } catch (error) {
    return {
      state: normalized,
      source: "local",
      backendAvailable: false,
      error: error instanceof Error ? error.message : "Backend save unavailable; local fallback was used.",
    };
  }
}

export async function loadActivityEvents() {
  return (await loadOperationalState()).state.activityEvents;
}

export async function saveActivityEvent(event: UniversalActivityEvent) {
  return patchOperationalState((state) => appendUniversalActivity(state, event));
}

export async function loadBusinessNotes() {
  return (await loadOperationalState()).state.businessNotes;
}

export async function saveBusinessNote(note: UniversalBusinessNote) {
  return patchOperationalState((state) => upsertUniversalBusinessNote(state, note));
}

export async function loadTicklers() {
  return (await loadOperationalState()).state.ticklers;
}

export async function saveTickler(tickler: UniversalTickler) {
  return patchOperationalState((state) => upsertUniversalTickler(state, tickler));
}

export async function loadAuditEvents() {
  return (await loadOperationalState()).state.auditTrail;
}

export async function saveAuditEvent(entry: UniversalAuditTrailEntry) {
  return patchOperationalState((state) => appendUniversalAuditEntry(state, entry));
}

export async function loadARRecoveryNotes() {
  return (await loadOperationalState()).state.arRecoveryNotes;
}

export async function saveARRecoveryNote(note: ARRecoveryActivityNote) {
  return patchOperationalState((state) => appendARRecoveryActivityNote(state, note));
}

export async function loadLetterDrafts() {
  return (await loadOperationalState()).state.collectionLetterDrafts;
}

export async function saveLetterDraft(draft: UniversalCollectionLetterDraft) {
  return patchOperationalState((state) => upsertCollectionLetterDraft(state, draft));
}

export async function loadLetterHistory() {
  return (await loadOperationalState()).state.collectionLetterHistory;
}

export async function saveLetterHistoryEvent(entry: UniversalCollectionLetterHistoryEntry) {
  return patchOperationalState((state) => appendCollectionLetterHistory(state, entry));
}

export function getMyOpenTicklers(state: UniversalOperationalFoundationState, assignedUser: string, workspace?: UniversalWorkspaceKey) {
  return state.ticklers.filter(
    (tickler) =>
      tickler.assignedUser === assignedUser &&
      tickler.status === "Open" &&
      (!workspace || tickler.workspace === workspace),
  );
}

export function getMyOverdueTicklers(state: UniversalOperationalFoundationState, assignedUser: string, workspace?: UniversalWorkspaceKey) {
  return state.ticklers.filter(
    (tickler) =>
      tickler.assignedUser === assignedUser &&
      (tickler.status === "Overdue" || isBeforeToday(tickler.dueDate)) &&
      tickler.status !== "Completed" &&
      (!workspace || tickler.workspace === workspace),
  );
}

export function getTicklersDueToday(state: UniversalOperationalFoundationState, workspace?: UniversalWorkspaceKey) {
  const today = new Date().toISOString().slice(0, 10);
  return state.ticklers.filter(
    (tickler) => tickler.dueDate === today && tickler.status !== "Completed" && (!workspace || tickler.workspace === workspace),
  );
}

export function getRecentActivityByWorkspace(
  state: UniversalOperationalFoundationState,
  workspace: UniversalWorkspaceKey,
  limit = 8,
) {
  return getActivityByRecord(state, workspace)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, limit);
}

export { getActivityByRecord, getAuditByRecord, getCollectionLetterHistoryByAccount, getNotesByRecord };

export async function loadMyOpenTicklers(assignedUser: string, workspace?: UniversalWorkspaceKey) {
  return getMyOpenTicklers((await loadOperationalState()).state, assignedUser, workspace);
}

export async function loadMyOverdueTicklers(assignedUser: string, workspace?: UniversalWorkspaceKey) {
  return getMyOverdueTicklers((await loadOperationalState()).state, assignedUser, workspace);
}

export async function loadTicklersDueToday(workspace?: UniversalWorkspaceKey) {
  return getTicklersDueToday((await loadOperationalState()).state, workspace);
}

export async function loadRecentActivityByWorkspace(workspace: UniversalWorkspaceKey, limit = 8) {
  return getRecentActivityByWorkspace((await loadOperationalState()).state, workspace, limit);
}

export async function loadActivityByRecord(workspace: UniversalWorkspaceKey, recordType?: UniversalRecordType, recordId?: string) {
  return getActivityByRecord((await loadOperationalState()).state, workspace, recordType, recordId);
}

export async function loadNotesByRecord(
  workspace: UniversalWorkspaceKey,
  recordType?: UniversalBusinessNote["recordType"],
  recordId?: string,
) {
  return getNotesByRecord((await loadOperationalState()).state, workspace, recordType, recordId);
}

export async function loadAuditByRecord(workspace: UniversalWorkspaceKey, recordType?: UniversalRecordType, recordId?: string, user?: string) {
  return getAuditByRecord((await loadOperationalState()).state, workspace, recordType, recordId, user);
}

export async function loadLetterActionsByAccount(accountId: string) {
  return getCollectionLetterHistoryByAccount((await loadOperationalState()).state, accountId);
}

async function patchOperationalState(mutator: (state: UniversalOperationalFoundationState) => UniversalOperationalFoundationState) {
  const current = await loadOperationalState();
  return saveOperationalState(mutator(current.state));
}

async function saveBackendOperationalState(state: UniversalOperationalFoundationState) {
  return saveBusinessCenterOperationalState(UNIVERSAL_OPERATIONAL_MODULE_KEY, createUniversalOperationalFoundationState(state), UNIVERSAL_OPERATIONAL_SCOPE);
}

function mergeOperationalStates(
  remote: UniversalOperationalFoundationState,
  local: UniversalOperationalFoundationState,
): UniversalOperationalFoundationState {
  return createUniversalOperationalFoundationState({
    activityEvents: mergeById(remote.activityEvents, local.activityEvents),
    businessNotes: mergeById(remote.businessNotes, local.businessNotes),
    ticklers: mergeById(remote.ticklers, local.ticklers),
    auditTrail: mergeById(remote.auditTrail, local.auditTrail),
    arRecoveryNotes: mergeById(remote.arRecoveryNotes, local.arRecoveryNotes),
    collectionLetterDrafts: mergeDrafts(remote.collectionLetterDrafts, local.collectionLetterDrafts),
    collectionLetterHistory: mergeById(remote.collectionLetterHistory, local.collectionLetterHistory),
    operationalRecords: mergeOperationalRecords(remote.operationalRecords, local.operationalRecords),
  });
}

function mergeById<T extends { id: string }>(primary: T[], fallback: T[]) {
  const items = new Map<string, T>();
  [...fallback, ...primary].forEach((item) => {
    items.set(item.id, item);
  });
  return Array.from(items.values());
}

function mergeDrafts(primary: UniversalCollectionLetterDraft[], fallback: UniversalCollectionLetterDraft[]) {
  const items = new Map<string, UniversalCollectionLetterDraft>();
  [...fallback, ...primary].forEach((draft) => {
    const current = items.get(draft.id);
    if (!current || new Date(draft.updatedAt).getTime() >= new Date(current.updatedAt).getTime()) {
      items.set(draft.id, draft);
    }
  });
  return Array.from(items.values());
}

function mergeOperationalRecords(
  primary: UniversalOperationalFoundationState["operationalRecords"],
  fallback: UniversalOperationalFoundationState["operationalRecords"],
) {
  const items = new Map<string, UniversalOperationalFoundationState["operationalRecords"][number]>();
  [...fallback, ...primary].forEach((record) => {
    const current = items.get(record.id);
    if (!current || new Date(record.updatedAt).getTime() >= new Date(current.updatedAt).getTime()) {
      items.set(record.id, record);
    }
  });
  return Array.from(items.values());
}

function isOperationalStateEmpty(state: UniversalOperationalFoundationState) {
  return (
    state.activityEvents.length === 0 &&
    state.businessNotes.length === 0 &&
    state.ticklers.length === 0 &&
    state.auditTrail.length === 0 &&
    state.arRecoveryNotes.length === 0 &&
    state.collectionLetterDrafts.length === 0 &&
    state.collectionLetterHistory.length === 0 &&
    state.operationalRecords.length === 0
  );
}

function isBeforeToday(date: string) {
  return date < new Date().toISOString().slice(0, 10);
}
